import { useReducer, useCallback } from 'react';

type IdentificationState = {
  identifications: Record<number, string | null>; // segmentIndex → playerId
  currentSegmentIndex: number;
  isComplete: boolean;
};

type Action =
  | { type: 'IDENTIFY'; segmentIndex: number; playerId: string }
  | { type: 'NEXT_SEGMENT' }
  | { type: 'PREV_SEGMENT' }
  | { type: 'GO_TO'; segmentIndex: number }
  | { type: 'RESET' };

type InitArg = {
  totalSegments: number;
  initialIdentifications?: Record<number, string>;
};

function createInitialState(arg: InitArg): IdentificationState {
  const identifications: Record<number, string | null> = {};
  for (let i = 0; i < arg.totalSegments; i++) {
    identifications[i] = arg.initialIdentifications?.[i] ?? null;
  }

  // Find first unidentified segment
  let firstUnidentified = 0;
  for (let i = 0; i < arg.totalSegments; i++) {
    if (identifications[i] == null) {
      firstUnidentified = i;
      break;
    }
  }

  return {
    identifications,
    currentSegmentIndex: firstUnidentified,
    isComplete: checkComplete(identifications, arg.totalSegments),
  };
}

function checkComplete(
  identifications: Record<number, string | null>,
  totalSegments: number,
): boolean {
  for (let i = 0; i < totalSegments; i++) {
    if (identifications[i] == null) return false;
  }
  return totalSegments > 0;
}

function reducer(
  state: IdentificationState,
  action: Action,
  totalSegments: number,
): IdentificationState {
  switch (action.type) {
    case 'IDENTIFY': {
      const identifications = {
        ...state.identifications,
        [action.segmentIndex]: action.playerId,
      };
      return {
        ...state,
        identifications,
        isComplete: checkComplete(identifications, totalSegments),
      };
    }
    case 'NEXT_SEGMENT': {
      const next = Math.min(state.currentSegmentIndex + 1, totalSegments - 1);
      return { ...state, currentSegmentIndex: next };
    }
    case 'PREV_SEGMENT': {
      const prev = Math.max(state.currentSegmentIndex - 1, 0);
      return { ...state, currentSegmentIndex: prev };
    }
    case 'GO_TO': {
      const idx = Math.max(0, Math.min(action.segmentIndex, totalSegments - 1));
      return { ...state, currentSegmentIndex: idx };
    }
    case 'RESET':
      return createInitialState({ totalSegments });
    default:
      return state;
  }
}

export function usePlayerIdentification(
  totalSegments: number,
  initialIdentifications?: Record<number, string>,
) {
  const [state, dispatch] = useReducer(
    (s: IdentificationState, a: Action) => reducer(s, a, totalSegments),
    { totalSegments, initialIdentifications },
    createInitialState,
  );

  const identify = useCallback((segmentIndex: number, playerId: string) => {
    dispatch({ type: 'IDENTIFY', segmentIndex, playerId });
  }, []);

  const next = useCallback(() => {
    dispatch({ type: 'NEXT_SEGMENT' });
  }, []);

  const prev = useCallback(() => {
    dispatch({ type: 'PREV_SEGMENT' });
  }, []);

  const goTo = useCallback((segmentIndex: number) => {
    dispatch({ type: 'GO_TO', segmentIndex });
  }, []);

  const reset = useCallback(() => {
    dispatch({ type: 'RESET' });
  }, []);

  return {
    state,
    identify,
    next,
    prev,
    goTo,
    reset,
    isComplete: state.isComplete,
    currentSegmentIndex: state.currentSegmentIndex,
    identifications: state.identifications,
  };
}
